import React, { useCallback, useEffect, useState } from "react";

import IndexNavbar from "components/Navbars/IndexNavbar";
import DarkFooter from "components/Footers/DarkFooter.js";
import {
  CoSponsorCard,
  FullSponsorRow,
  SponsorError,
  SponsorSkeleton,
} from "components/Sponsors/SponsorLists";
import useSponsors from "hooks/useSponsors";
import { filterSponsors } from "utils/sponsors";

function SponsorsPage() {
  const { years, selectedYear, data, loading, failed, selectYear, retry } =
    useSponsors();
  const [query, setQuery] = useState("");
  const [highlightId, setHighlightId] = useState("");

  useEffect(() => {
    document.body.classList.add("sidebar-collapse");
    document.documentElement.classList.remove("nav-open");
    window.scrollTo(0, 0);
    document.body.scrollTop = 0;

    return function cleanup() {
      document.body.classList.remove("sidebar-collapse");
    };
  }, []);

  const handleJump = useCallback((anchorId) => {
    const target = document.getElementById(anchorId);
    if (target == null) {
      return;
    }
    target.scrollIntoView({ behavior: "smooth", block: "center" });
    setHighlightId(anchorId);
    window.setTimeout(() => setHighlightId(""), 2200);
  }, []);

  const visible = filterSponsors(data, query);
  const hasResults =
    visible != null &&
    (visible.fullSponsors.length > 0 || visible.coSponsors.length > 0);

  return (
    <>
      <IndexNavbar />
      <div className="wrapper sp-page">
        <header className="sp-hero">
          <div className="sp-wrap">
            <p className="sp-eyebrow">Durgotsav</p>
            <h1>Our Sponsors</h1>
            <p className="sp-hero-lead">
              The families and friends whose offerings light up every day of the
              Pujo. Thank you.
            </p>

            {years.length > 1 && (
              <div className="sp-years" role="group" aria-label="Choose a year">
                {years.map((year) => (
                  <button
                    className="sp-year"
                    type="button"
                    key={year}
                    aria-pressed={year === selectedYear}
                    onClick={() => selectYear(year)}
                  >
                    {year}
                  </button>
                ))}
              </div>
            )}
            {years.length === 1 && (
              <div className="sp-year-static">
                <small>Year</small> {years[0]}
              </div>
            )}
          </div>
        </header>

        <div className="sp-wrap sp-body">
          {loading && (
            <>
              <div className="sp-block">
                <div className="sp-block-head">
                  <h2>Full Sponsorship</h2>
                </div>
                <SponsorSkeleton rows={6} />
              </div>
              <div className="sp-block">
                <div className="sp-block-head">
                  <h2>Co-Sponsorship</h2>
                </div>
                <SponsorSkeleton rows={2} />
              </div>
            </>
          )}

          {!loading && failed && <SponsorError onRetry={retry} />}

          {!loading && !failed && data != null && (
            <>
              <div className="sp-filter">
                <span className="sp-filter-ico">&#9906;</span>
                <input
                  type="search"
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  placeholder="Find an offering or a sponsor"
                  aria-label="Find an offering or a sponsor"
                />
              </div>

              {!hasResults && (
                <p className="sp-empty">
                  No offering or sponsor matches &ldquo;{query}&rdquo;.
                </p>
              )}

              {visible.fullSponsors.length > 0 && (
                <div className="sp-block">
                  <div className="sp-block-head">
                    <h2>Full Sponsorship</h2>
                    <span className="sp-count">
                      {visible.fullSponsors.length} offerings
                    </span>
                    <p>One sponsor carried each of these in full.</p>
                  </div>
                  <div className="sp-grid">
                    {visible.fullSponsors.map((entry, index) => (
                      <FullSponsorRow
                        entry={entry}
                        onJump={handleJump}
                        key={`${entry.offering}-${index}`}
                      />
                    ))}
                  </div>
                </div>
              )}

              {visible.coSponsors.length > 0 && (
                <div className="sp-block">
                  <div className="sp-block-head">
                    <h2>Co-Sponsorship</h2>
                    <span className="sp-count">
                      {visible.coSponsors.length} offerings
                    </span>
                    <p>Offerings funded together by the community.</p>
                  </div>
                  <div className="sp-cards">
                    {visible.coSponsors.map((entry) => (
                      <CoSponsorCard
                        entry={entry}
                        highlighted={entry.anchorId === highlightId}
                        key={entry.anchorId}
                      />
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        <DarkFooter />
      </div>
    </>
  );
}

export default SponsorsPage;
